import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { faArrowDown, faArrowUp, faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
	CAMPAIGN_ROUTING_MAX_CONDITIONS,
	CAMPAIGN_ROUTING_MAX_VALUES,
} from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

import { ROUTING_I18N } from "./utils/campaignRoutingFields";
import {
	canAddCondition,
	DraftCondition,
	DraftRule,
	ruleProblemOf,
} from "./utils/campaignRoutingDraft";
import { RoutingCampaign } from "./utils/campaignRoutingTargets";
import { RoutingCampaignPicker } from "./RoutingCampaignPicker";
import { RoutingConditionRow } from "./RoutingConditionRow";

interface RoutingRuleCardProps {
	listId: number;
	index: number;
	count: number;
	rule: DraftRule;
	campaigns: readonly RoutingCampaign[];
	onChange: (rule: DraftRule) => void;
	onAddCondition: () => void;
	onMove: (delta: -1 | 1) => void;
	onRemove: () => void;
}

const IconButton: FunctionComponent<{
	label: string;
	icon: typeof faXmark;
	testId: string;
	disabled?: boolean;
	onClick: () => void;
}> = ({ label, icon, testId, disabled, onClick }) => (
	<button
		aria-label={label}
		className="cursor-pointer rounded p-1 text-text-meta hover:bg-background-light disabled:cursor-not-allowed disabled:opacity-40"
		data-testid={testId}
		disabled={disabled}
		type="button"
		onClick={onClick}
	>
		<Icon className="h-4 w-4" icon={icon} />
	</button>
);

/** One numbered rule card: its conditions (AND), its campaign, reorder and remove. */
export const RoutingRuleCard: FunctionComponent<RoutingRuleCardProps> = ({
	listId,
	index,
	count,
	rule,
	campaigns,
	onChange,
	onAddCondition,
	onMove,
	onRemove,
}) => {
	const { t } = useTranslation();
	const problem = ruleProblemOf(rule);

	const changeCondition = (uid: string, next: Omit<DraftCondition, "uid">) =>
		onChange({
			...rule,
			conditions: rule.conditions.map((condition) =>
				condition.uid === uid ? { ...next, uid } : condition
			),
		});

	const removeCondition = (uid: string) =>
		onChange({ ...rule, conditions: rule.conditions.filter((condition) => condition.uid !== uid) });

	return (
		<div
			className="flex flex-col gap-3 rounded-lg border border-border-light bg-brand-white p-4"
			data-testid="routing-rule"
		>
			<div className="flex items-center justify-between gap-2">
				<span className="text-sm font-semibold text-text">{index + 1}.</span>
				<div className="flex items-center gap-1">
					<IconButton
						disabled={index === 0}
						icon={faArrowUp}
						label={t(`${ROUTING_I18N}.editor.moveUp`)}
						testId="routing-rule-up"
						onClick={() => onMove(-1)}
					/>
					<IconButton
						disabled={index === count - 1}
						icon={faArrowDown}
						label={t(`${ROUTING_I18N}.editor.moveDown`)}
						testId="routing-rule-down"
						onClick={() => onMove(1)}
					/>
					<IconButton
						icon={faXmark}
						label={t(`${ROUTING_I18N}.editor.removeRule`)}
						testId="routing-rule-remove"
						onClick={onRemove}
					/>
				</div>
			</div>
			<div className="flex flex-col gap-2">
				{rule.conditions.map((condition, conditionIndex) => (
					<div className="flex flex-col gap-2" key={condition.uid}>
						{conditionIndex > 0 && (
							<span className="self-center text-xs font-semibold text-text-meta">
								— {t(`${ROUTING_I18N}.editor.and`)} —
							</span>
						)}
						<RoutingConditionRow
							condition={condition}
							listId={listId}
							onChange={(next) => changeCondition(condition.uid, next)}
							onRemove={
								rule.conditions.length > 1 ? () => removeCondition(condition.uid) : undefined
							}
						/>
					</div>
				))}
			</div>
			<div className="flex items-center gap-2">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					disabled={!canAddCondition(rule)}
					size={ButtonSize.XS}
					testId="routing-add-condition"
					title={t(`${ROUTING_I18N}.editor.addCondition`)}
					onClick={onAddCondition}
				/>
				{!canAddCondition(rule) && (
					<span className="text-xs text-text-meta">
						{t(`${ROUTING_I18N}.editor.limitConditions`, { max: CAMPAIGN_ROUTING_MAX_CONDITIONS })}
					</span>
				)}
			</div>
			<div className="border-t border-border-light pt-2">
				<RoutingCampaignPicker
					campaignUuid={rule.campaignUuid}
					campaigns={campaigns}
					onChange={(campaignUuid) => onChange({ ...rule, campaignUuid })}
				/>
			</div>
			{problem && (
				<p className="text-xs text-status-error" data-testid="routing-rule-problem">
					{t(`${ROUTING_I18N}.editor.incomplete`, {
						number: index + 1,
						problem: t(`${ROUTING_I18N}.editor.problems.${problem}`, {
							max:
								problem === "tooManyConditions"
									? CAMPAIGN_ROUTING_MAX_CONDITIONS
									: CAMPAIGN_ROUTING_MAX_VALUES,
						}),
					})}
				</p>
			)}
		</div>
	);
};
